// Runtime-agnostic (browser + Node/Edge) request/promise deadline utilities. A
// bare `fetch` (or any awaited promise) has NO timeout: if the peer accepts the
// connection but never responds, it hangs forever — which is how the checkout
// "Confirming your booking…" overlay could stick indefinitely. Bounding every
// hop guarantees a stalled call fails fast and recoverably instead.

export class TimeoutError extends Error {
  constructor(message = "The request timed out") {
    super(message);
    this.name = "TimeoutError";
  }
}

export async function fetchWithTimeout(
  input: string | URL | Request,
  init: RequestInit = {},
  timeoutMs = 30_000,
): Promise<Response> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  // Respect a caller-supplied signal instead of silently dropping it: chain it
  // to our controller so external cancellation (component unmount, navigation)
  // still aborts the request. The naive `{ ...init, signal: controller.signal }`
  // would overwrite `init.signal`.
  const external = init.signal ?? undefined;
  const onExternalAbort = (): void => controller.abort();
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener("abort", onExternalAbort, { once: true });
  }

  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (err) {
    // Only OUR deadline maps to TimeoutError. An external/navigation abort (or
    // any other failure) propagates unchanged so callers don't show a false
    // "timed out" state for a user-initiated cancellation.
    if (timedOut) throw new TimeoutError();
    throw err;
  } finally {
    clearTimeout(timer);
    if (external) external.removeEventListener("abort", onExternalAbort);
  }
}
